import {css, StyleSheet} from 'aphrodite';
import {produce} from 'immer';
import React, {useMemo} from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {assert} from 'Asserts';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {GetScratchFile, GetScratchFileVariables} from 'pipelines/files/network/__generated/GetScratchFile';
import {GET_SCRATCH_FILE_QUERY} from 'pipelines/files/network/GetScratchFile';
import ScratchFilePaneContent from 'pipelines/files/ScratchFilePaneContent';
import {ScratchFileContent} from 'pipelines/files/ScratchFileTypes';
import {assertPipelineStepRawDataIsKind} from 'pipelines/PipelineTypes';

import DataViewContext, {useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';
import FailedView from './FailedView';
import getDownloadFileAction from './file-view/getDownloadFileAction';
import LoadingView from './LoadingView';

export interface FileViewWithFileProps {
  file: ScratchFileContent;
}

export interface FileViewWithFileIdProps {
  fileId: string;
}

export type FileViewProps = FileViewWithFileProps | FileViewWithFileIdProps | Record<string, never>;

function isFileViewWithFile(props: FileViewProps): props is FileViewWithFileProps {
  return props.hasOwnProperty('file');
}

function isFileViewWithFileId(props: FileViewProps): props is FileViewWithFileIdProps {
  return props.hasOwnProperty('fileId');
}

/**
 * Render view for files.
 *
 * Can be used in three ways:
 * 1. With a `file` prop: Renders the provided file object directly.
 * 2. With a `fileId` prop: Queries and renders the file by ID.
 * 3. With no props: Uses analysisView from context.
 *
 * @example
 * // With file object
 * <FileView file={fileObject} />
 *
 * // With file ID
 * <FileView fileId="sf_abc123" />
 *
 * // With analysisView from DataViewContext
 * <FileView />
 */
export default function FileView(props: FileViewProps = {}): JSX.Element {
  if (isFileViewWithFile(props)) {
    return <FileViewWithFile file={props.file} />;
  }

  if (isFileViewWithFileId(props)) {
    return <FileViewWithFileId fileId={props.fileId} />;
  }

  return <FileViewWithAnalysisView />;
}

function getFileDataViewContextOverrides(
  dataViewContextValue: ReturnType<typeof useDataViewContext>,
  file: ScratchFileContent
): ReturnType<typeof useDataViewContext> {
  const {metadataContent} = dataViewContextValue;

  return produce(dataViewContextValue, (draftContextValue) => {
    draftContextValue.actions.overflowActions.push(getDownloadFileAction(file));
    draftContextValue.dataTestAttrs = {component: 'FileView', element: 'root'};
    draftContextValue.metadataContent.gridCells = [
      {component: file.filename, label: 'File name'},
      ...metadataContent.gridCells,
    ];
  });
}

function FileViewWithFile({file}: FileViewWithFileProps): JSX.Element {
  const dataViewContextValue = useDataViewContext();

  const newDataViewContextValue = useMemo(
    () => getFileDataViewContextOverrides(dataViewContextValue, file),
    [dataViewContextValue, file]
  );

  return (
    <DataViewContext.Provider value={newDataViewContextValue}>
      <DataViewPane>
        <div className={css(styles.dataViewPaneContent)}>
          <ScratchFilePaneContent scratchFile={file} />
        </div>
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

function FileViewWithFileId({fileId}: FileViewWithFileIdProps): JSX.Element {
  return (
    <BenchlingQuery<GetScratchFile, GetScratchFileVariables>
      query={GET_SCRATCH_FILE_QUERY}
      variables={{id: fileId}}
      renderLoading={() => <LoadingView />}
    >
      {(data) =>
        isInaccessible(data.scratchFile) ? (
          <FailedView statusMessage="You do not have access to this file." />
        ) : (
          <FileViewWithFile file={data.scratchFile} />
        )
      }
    </BenchlingQuery>
  );
}

function FileViewWithAnalysisView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView} = dataViewContextValue;

  assert(analysisView != null, 'analysisView must be provided in context when using FileView');

  const stepOutputReference = analysisView.lineage[analysisView.lineage.length - 1].stepOutputReference!;
  const file = stepOutputReference.outputStepData.rawDatas[stepOutputReference.rawDataIndex!];

  assertPipelineStepRawDataIsKind(file, PipelineStepDataKind.FILE);

  const newDataViewContextValue = useMemo(
    () => getFileDataViewContextOverrides(dataViewContextValue, file),
    [dataViewContextValue, file]
  );

  return (
    <DataViewContext.Provider value={newDataViewContextValue}>
      <DataViewPane>
        <div className={css(styles.dataViewPaneContent)}>
          <ScratchFilePaneContent scratchFile={file} />
        </div>
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

const styles = StyleSheet.create({
  dataViewPaneContent: {
    padding: ANALYSIS_TOOL_SPACING,
    height: '100%',
  },
});
